import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  inject,
  DestroyRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LucideAngularModule, X, Crown, Sparkles, Star, Rocket, PartyPopper, Loader2 } from 'lucide-angular';
import { ConfettiCanvasComponent } from '../../../common/components/confetti-canvas/confetti-canvas';
import { AuthService } from '../../../common/auth/auth.service';

@Component({
  selector: 'app-subscription-success-modal',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, ConfettiCanvasComponent],
  templateUrl: './subscription-success-modal.html',
  styleUrl: './subscription-success-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubscriptionSuccessModalComponent implements AfterViewInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(ConfettiCanvasComponent) confettiCanvas?: ConfettiCanvasComponent;

  private readonly isOpen$$ = new BehaviorSubject<boolean>(false);
  protected readonly isOpen$ = this.isOpen$$.asObservable();

  private readonly productName$$ = new BehaviorSubject<string>('Premium');
  protected readonly productName$ = this.productName$$.asObservable();

  // Loading state for subscription verification
  private readonly isVerifying$$ = new BehaviorSubject<boolean>(false);
  protected readonly isVerifying$ = this.isVerifying$$.asObservable();

  // Progress percentage (0-100) for the progress bar
  private readonly progress$$ = new BehaviorSubject<number>(0);
  protected readonly progress$ = this.progress$$.asObservable();

  // Hide confetti after animation completes
  private readonly showConfetti$$ = new BehaviorSubject<boolean>(false);
  protected readonly showConfetti$ = this.showConfetti$$.asObservable();

  // Polling subscription
  private pollingSubscription: Subscription | null = null;
  private progressInterval: ReturnType<typeof setInterval> | null = null;
  private readonly MAX_POLL_TIME_MS = 10000; // 10 seconds max
  private readonly POLL_INTERVAL_MS = 1000; // 1 second interval

  @Input()
  set isOpen(value: boolean) {
    const wasOpen = this.isOpen$$.value;
    this.isOpen$$.next(value);

    // Start verification polling when modal opens
    if (value && !wasOpen) {
      this.startVerificationPolling();
    }

    // Reset state when modal closes
    if (!value) {
      this.stopPolling();
      this.showConfetti$$.next(false);
      this.isVerifying$$.next(false);
      this.progress$$.next(0);
    }
  }

  @Input()
  set productName(value: string) {
    this.productName$$.next(value);
  }

  @Output() readonly closed = new EventEmitter<void>();

  protected readonly icons = {
    X,
    Crown,
    Sparkles,
    Star,
    Rocket,
    PartyPopper,
    Loader2,
  };

  protected readonly features = [
    'Unlimited lesson plan generations',
    'Export to PDF, PPTX & Google Slides',
    'Premium AI-powered features',
    'Priority customer support',
  ];

  ngAfterViewInit(): void {
    // No additional trigger needed - handled in isOpen setter
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  protected close(): void {
    // Don't allow closing while verifying
    if (this.isVerifying$$.value) {
      return;
    }
    this.closed.emit();
  }

  private startVerificationPolling(): void {
    this.isVerifying$$.next(true);
    this.progress$$.next(0);
    const startTime = Date.now();

    // Start progress animation (updates every 100ms for smooth animation)
    this.startProgressAnimation(startTime);

    // Do an immediate first check
    this.checkSubscriptionStatus(startTime);

    // Then start interval polling
    this.pollingSubscription = interval(this.POLL_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.checkSubscriptionStatus(startTime);
      });
  }

  /**
   * Animate progress bar with exponential easing
   * Starts fast and slows down as it approaches 95% (never reaches 100% until complete)
   */
  private startProgressAnimation(startTime: number): void {
    this.progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = this.calculateExponentialProgress(elapsed);
      this.progress$$.next(progress);
    }, 100); // Update every 100ms for smooth animation
  }

  /**
   * Calculate exponential progress that approaches but never reaches 95%
   * Uses exponential ease-out formula: 1 - e^(-t/tau)
   * This gives fast initial progress that slows down over time
   */
  private calculateExponentialProgress(elapsedMs: number): number {
    // Time constant (tau) - controls how fast progress slows down
    // Higher value = slower initial progress but more gradual slowdown
    const tau = 3000; // 3 seconds time constant

    // Exponential approach to 95% (leave room for completion jump to 100%)
    const maxProgress = 95;
    const progress = maxProgress * (1 - Math.exp(-elapsedMs / tau));

    return Math.min(progress, maxProgress);
  }

  private checkSubscriptionStatus(startTime: number): void {
    const elapsedTime = Date.now() - startTime;

    // Check if we've exceeded max poll time
    if (elapsedTime >= this.MAX_POLL_TIME_MS) {
      this.onVerificationComplete();
      return;
    }

    // Fetch fresh profile from API
    this.authService.refreshProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          // Check if subscription is now active
          if (this.authService.hasPremium()) {
            this.onVerificationComplete();
          }
        },
        error: () => {
          // On error, continue polling until timeout
          console.warn('Profile fetch failed, continuing to poll...');
        }
      });
  }

  private onVerificationComplete(): void {
    // Set progress to 100% before stopping
    this.progress$$.next(100);

    this.stopPolling();
    this.isVerifying$$.next(false);

    // Show celebration
    this.showConfetti$$.next(true);
    setTimeout(() => {
      this.triggerCelebration();
    }, 200);
    // Hide confetti after animation completes
    setTimeout(() => {
      this.showConfetti$$.next(false);
    }, 1000);
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  private triggerCelebration(): void {
    if (this.confettiCanvas) {
      this.confettiCanvas.createBurst();
    }
  }
}

